import { Component, OnInit } from '@angular/core';
import { ApiService } from "../../api.service";
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { CommonService } from "../../shared/common.service";
import { Study, Crc } from '../../study.model';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
declare var bootstrap: any;

@Component({
  selector: 'app-study-crc-list',
  templateUrl: './study-crc-list.component.html',
  styleUrls: ['./study-crc-list.component.scss']
})
export class StudyCRCListComponent implements OnInit {
  
  // ===========================================
  // CORE COMPONENT PROPERTIES
  // ===========================================
  
  // Data properties
  data: any[] = [];
  users: any[] = [];
  crcsList: Crc[] = [];
  Data: any;
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  pages = 0;
  totalUsers = 0;
  pageRange: number[] = [];
  maxPagesToShow: number = 4;
  loading: boolean = false;
  
  // Search and filter properties
  searchCrcName: string = '';
  searchStudyName: string = '';
  searchLocation: string = '';
  searchAssignedBy: string = '';
  
  // CRC Assignment Modal properties
  selectedStudy: any = null;
  assignCrcForm: FormGroup;
  assignCrcValidationMessage: string = '';
  isEditMode: boolean = false;
  private assignCrcModalInstance: any | null = null;
  
  // Custom dropdown properties
  showCustomDropdown: boolean = false;
  searchTerm: string = '';
  filteredCrcsList: any[] = [];
  currentStudySiteLocation: string = '';
  
  // Dropdown settings
  dropdownSettings = {};
  
  // Freeze/Unfreeze properties
  currentStudy: any = null;
  isFreezing: boolean = true;
  freezeEmail: string = '';
  freezePassword: string = '';
  freezeReason: string = '';
  freezeError: string = '';
  showFreezePassword: boolean = false;
  
  // Lock properties
  lockEmail: string = '';
  lockPassword: string = '';
  lockReason: string = '';
  lockError: string = '';
  showLockPassword: boolean = false;

  // User
  currentUser: any = null;
  
  // ===========================================
  // CONSTRUCTOR & INITIALIZATION
  // ===========================================
  
  constructor(
    private apiService: ApiService,
    public commonService: CommonService,
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute 
  ) {
    this.assignCrcForm = this.fb.group({
      selectedCrcs: [[], Validators.required]
    });
  }

  ngOnInit(): void {
    this.initializeUserData();
    this.currentUser = this.Data;
    this.setupQueryParams();
    this.loadCrcsList();
    this.initializeDropdownSettings();
  }

  // ===========================================
  // INITIALIZATION METHODS
  // ===========================================
  
  /**
   * Initialize user data and check authentication
   */
  private initializeUserData(): void {
    let userData = this.commonService.getUserData();
    this.Data = userData;

    if (userData == null) {
      this.commonService.logout();
    }
  }

  canManageFreezeLock(): boolean {
    const designation = this.Data?.designation || this.currentUser?.designation || '';
    return designation === 'Monitor' || designation === 'Data Manager';
  }

  /**
   * Setup query parameters for pagination and filtering
   */
  private setupQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      this.currentPage = +params['page'] || 1;
      this.searchCrcName = params['searchCrcName'] || '';
      this.searchStudyName = params['searchStudyName'] || '';
      this.searchLocation = params['searchLocation'] || '';
      this.searchAssignedBy = params['searchAssignedBy'] || '';

      this.loadUsers(this.currentPage);
    });
  }

  /**
   * Initialize dropdown settings for CRC selection
   */
  private initializeDropdownSettings(): void {
    this.dropdownSettings = {
      singleSelection: false,
      idField: 'item_id',
      textField: 'item_text',
      selectAllText: 'Select All',
      unSelectAllText: 'Unselect All',
      itemsShowLimit: 3,
      allowSearchFilter: true,
      enableCheckAll: true,
      showCheckboxes: true,
      badgeShowLimit: 5,
      classes: 'crc-dropdown-custom',
      customTemplate: true,
      maxHeight: 200,
      searchPlaceholderText: 'Search users...',
      noDataLabel: 'No users found'
    };
  }

  // ===========================================
  // DATA LOADING METHODS
  // ===========================================
  
  /**
   * Load users with pagination and filtering using getAllStudiesWithCRC
   */
  loadUsers(page: number): void {
    this.loading = true;

    console.log('🚀 [loadUsers] Loading users with getAllStudiesWithCRC API');
    console.log('📊 [loadUsers] Parameters:', {
      page,
      searchCrcName: this.searchCrcName,
      searchStudyName: this.searchStudyName,
      searchLocation: this.searchLocation,
      searchAssignedBy: this.searchAssignedBy
    });

    // Provide default empty values for filters
    const searchCrcName = this.searchCrcName || '';
    const searchStudyName = this.searchStudyName || '';
    const designationFilter = this.searchLocation || '';
    const statusFilter = this.searchAssignedBy || '';

    const userId = this.Data.id;
    const designation = this.Data.designation;

    // Use getAllStudiesWithCRC API
    this.apiService.getAllStudiesWithCRC(
      userId,
      page,
      this.pageSize,
      searchStudyName,
      statusFilter,
      '1', // is_active
      'Started', // status
      designation
    ).subscribe(
      (response: any) => {
        console.log('✅ [loadUsers] getAllStudiesWithCRC response received:', response);
        
        // Process the response data
        this.data = response.data || response || [];
        
        // Initialize filtered data with all data (no filtering on load)
        this.filteredData = [...this.data];
        
        const totalFromApi = response.totalRecords || response.totalUsers;
        this.totalUsers = totalFromApi ?? this.filteredData.length;
        this.pages = response.totalPages ?? Math.ceil(this.totalUsers / this.pageSize);
        this.currentPage = page;
        
        console.log('📊 [loadUsers] Processed data:', {
          dataLength: this.data.length,
          filteredDataLength: this.filteredData.length,
          totalPages: this.pages,
          totalUsers: this.totalUsers,
          currentPage: this.currentPage
        });
        
        const studyIds = this.data.map((d: any) => d.study_id).filter(Boolean);
        if (studyIds.length === 0) {
          this.data.forEach((d: any) => { d.is_frozen = false; d.is_locked = false; });
          this.updatePageRange();
          this.updatePaginatedData();
          this.loading = false;
          return;
        }
        this.apiService.fetchLockedFields({ locked_data_id: studyIds, data_type: 'study' }).subscribe(
          (res: any) => {
            const rows = res.data || [];
            const map: Record<string, { is_frozen: boolean; is_locked: boolean }> = {};
            rows.forEach((r: any) => {
              map[String(r.locked_data_id)] = {
                is_frozen: !!(r.is_freezed ?? r.is_frozen),
                is_locked: !!r.is_locked
              };
            });
            this.data.forEach((d: any) => {
              const m = map[String(d.study_id)];
              d.is_frozen = m ? m.is_frozen : false;
              d.is_locked = m ? m.is_locked : false;
            });
            this.updatePageRange();
            this.updatePaginatedData();
            this.loading = false;
          },
          (err) => { console.error('Error fetching study lock status:', err); this.updatePageRange(); this.updatePaginatedData(); this.loading = false; }
        );
      },
      error => {
        console.error('❌ [loadUsers] Error fetching data:', error);
        this.data = [];
        alert(error.error.error);
        this.loading = false;
      }
    );
  }

  /**
   * Process API response and organize data
   */
  private processApiResponse(studiesResponse: any, crcResponse: any): void {
    // Create a map of studies data for quick lookup
    const studiesMap = new Map();
    studiesResponse.data.forEach(study => {
      studiesMap.set(study.id, study);
    });

    // Create individual rows for each CRC assignment
    this.users = this.processCrcStudiesData(crcResponse, studiesMap);

    // Add studies that don't have any CRC assignments
    studiesResponse.data.forEach(study => {
      const hasCrcAssignment = crcResponse.data.some(crcStudy => crcStudy.study_id === study.id);
      if (!hasCrcAssignment) {
        this.users.push({
          ...study,
          id: study.id,
          study_id: study.id,
          study_name: study.study_name,
          site_location: this.getSiteLocation(study),
          template_name: this.getTemplateName(study),
          template_list: study.template_list || [],
          site_list: study.site_list || [],
          assigned_users: [],
          has_assignments: false,
          is_crc_row: false,
          study_crc_id: null,
          crc_id: null,
          crc_name: 'Not Assigned'
        });
      }
    });

    // Process and group the data by study_name, study_code, and site_location
    this.data = this.groupUsersByStudyAndLocation(crcResponse.data, studiesMap);
    this.pages = crcResponse.totalPages;
    this.totalUsers = crcResponse.totalRecords;

    this.updatePageRange();
  }

  /**
   * Load CRC list for dropdown selection
   */
  loadCrcsList(): void {
    const userId = this.Data?.id;
    if (!userId) {
      console.error('User ID not available');
      return;
    }

    // Use the getUserList method with userId to fetch users created by the current user
    this.apiService.getUserList(1, 1000, '', '', '', '', userId).subscribe(
      (response: any) => {
        const allUsers = response['data'];
        const activeUsers = allUsers.filter(user => user.status === 'Active');
        
        // Map users created by the current user
        const createdUsers = activeUsers.map(user => {
          const initials = `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase();
          const fullName = `${user.first_name} ${user.last_name}`;
          return {
            item_id: user.id,
            item_text: `${user.id} - ${user.first_name} ${user.last_name} (${user.designation})`,
            initials: initials,
            full_name: fullName,
            first_name: user.first_name,
            last_name: user.last_name,
            designation: user.designation,
            site_location: user.site_location || user.location || 'Unknown Location'
          };
        });

        // Add the current user (creator) to the list if they are active
        const currentUser = this.Data;
        if (currentUser && currentUser.status === 'Active') {
          const currentUserInitials = `${currentUser.first_name?.charAt(0) || ''}${currentUser.last_name?.charAt(0) || ''}`.toUpperCase();
          const currentUserFullName = `${currentUser.first_name} ${currentUser.last_name}`;
          
          const currentUserEntry = {
            item_id: currentUser.id,
            item_text: `${currentUser.id} - ${currentUser.first_name} ${currentUser.last_name} (${currentUser.designation})`,
            initials: currentUserInitials,
            full_name: currentUserFullName,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            designation: currentUser.designation,
            site_location: currentUser.site_location || currentUser.location || 'Unknown Location'
          };

          // Check if current user is already in the list (in case they created themselves)
          const isCurrentUserInList = createdUsers.some(user => user.item_id === currentUser.id);
          if (!isCurrentUserInList) {
            createdUsers.unshift(currentUserEntry); // Add to the beginning of the list
          }
        }
        
        this.crcsList = createdUsers;
        this.filteredCrcsList = [...this.crcsList];
        console.log('✅ [loadCrcsList] Loaded users created by current user + creator:', this.crcsList.length);
      },
      (error) => {
        console.error('❌ [loadCrcsList] Error loading user list:', error);
      }
    );
  }

  // ===========================================
  // PAGINATION METHODS
  // ===========================================
  
  /**
   * Update the visible page range based on the current page
   */
  updatePageRange(): void {
    this.pageRange = [];
    const startPage = Math.floor((this.currentPage - 1) / this.maxPagesToShow) * this.maxPagesToShow + 1;
    const endPage = Math.min(startPage + this.maxPagesToShow - 1, this.pages);

    for (let i = startPage; i <= endPage; i++) {
      this.pageRange.push(i);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.pages) {
      this.currentPage++;
      this.updatePageRange();
      this.loadUsers(this.currentPage);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePageRange();
      this.loadUsers(this.currentPage);
    }
  }

  goToPage(pageIndex: number): void {
    if (pageIndex > 0 && pageIndex <= this.pages) {
      this.currentPage = pageIndex;
      this.updatePageRange();
      this.loadUsers(this.currentPage);
    }
  }

  // ===========================================
  // SEARCH AND FILTER METHODS
  // ===========================================
  
  /**
   * Clear all search filters
   */
  clear(): void {
    this.searchCrcName = '';
    this.searchStudyName = '';
    this.searchLocation = '';
    this.searchAssignedBy = '';
    
    // Show all data when cleared
    this.filteredData = [...this.data];
    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;
    this.currentPage = 1;
    this.updatePageRange();
    this.updatePaginatedData();
  }

  /**
   * Update query parameters for URL state management
   */
  updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.currentPage,
        searchCrcName: this.searchCrcName,
        searchStudyName: this.searchStudyName,
        searchLocation: this.searchLocation,
        searchAssignedBy: this.searchAssignedBy
      },
      queryParamsHandling: 'merge'
    });
  }

  // ===========================================
  // NAVIGATION METHODS
  // ===========================================
  
  /**
   * Navigate to study detail page
   */
  openStudyDetail(study: any): void {
    let studyCrcId = null;
    if (study.assigned_users && study.assigned_users.length > 0) {
      studyCrcId = study.assigned_users[0].study_crc_id;
    }

    console.log('study', study);
    
    const queryParams = { 
      name: study.study_name, 
      studyId: study.id, 
      centers: study.site_location, 
      studyCrcId: studyCrcId, 
      template_name: study.template_name,
      template_id: study.template_list[0].id
    };
    this.router.navigate(['/study-details', study.id], {queryParams});
  }

  EditStudy(index: number): void {
    this.router.navigate(['/study-crc-edit', index], { queryParams: { page: this.currentPage } });
  }

  viewStudy(index: number): void {
    this.router.navigate(['/study-crc-view/'+index], { queryParams: { page: this.currentPage } });
  }

  VisitStudy(index: number): void {
    this.router.navigate(['/view-visits/'+index], { queryParams: { page: this.currentPage } });
  }

  MonitorStudy(index: number): void {
    this.router.navigate(['/study-crc-monitoring/'+index], { queryParams: { page: this.currentPage } });
  }

  // ===========================================
  // CRC ASSIGNMENT METHODS
  // ===========================================
  
  /**
   * Open assign CRC modal
   */
  openAssignCrcModal(study: any): void {
    // Check if study is frozen or locked
    if (study.is_frozen || study.is_locked) {
      alert('This study is ' + (study.is_locked ? 'locked' : 'frozen') + '. No changes are allowed.');
      return;
    }
    
    this.selectedStudy = {
      id: study.study_id || study.id,
      study_name: study.study_name,
      code: study.study_code || study.code,
      template_list: study.template_list || [],
      site_list: study.site_list || [],
      study_status: study.study_status,
      crcs_list: study.assigned_users || []
    };
    this.assignCrcForm.reset();
    this.assignCrcValidationMessage = '';
    this.isEditMode = false;
    this.showCustomDropdown = false;
    this.searchTerm = '';
    this.currentStudySiteLocation = study.site_location || '';
    this.filteredCrcsList = this.filterUsersBySiteLocation(study.site_location);
    
    // Pre-populate with existing CRCs if any
    if (study.crcs_list && study.crcs_list.length > 0) {
      const existingCrcs = study.crcs_list.map(crc => ({
        item_id: crc.id,
        item_text: `${crc.id} - ${crc.name} (${crc.designation || 'User'})`
      }));
      this.assignCrcForm.patchValue({
        selectedCrcs: existingCrcs
      });
    }

    this.showModal();
  }

  /**
   * Edit CRC assignments - allows both adding and removing CRCs
   */
  editCrcAssignments(study: any): void {
    // Check if study is frozen or locked
    if (study.is_frozen || study.is_locked) {
      alert('This study is ' + (study.is_locked ? 'locked' : 'frozen') + '. No changes are allowed.');
      return;
    }
    
    this.selectedStudy = {
      id: study.study_id,
      study_name: study.study_name,
      code: study.study_code,
      template_list: study.template_list || [],
      site_list: study.site_list || [],
      study_status: study.study_status,
      crcs_list: study.assigned_users || []
    };
    
    this.assignCrcForm.reset();
    this.assignCrcValidationMessage = '';
    this.isEditMode = true;
    this.showCustomDropdown = false;
    this.searchTerm = '';
    this.currentStudySiteLocation = study.site_location || '';
    this.filteredCrcsList = this.filterUsersBySiteLocation(study.site_location);
    
    // Pre-populate with existing assigned users
    if (study.assigned_users && study.assigned_users.length > 0) {
      const existingCrcs = study.assigned_users.map(crc => {
        const matchingCrc = this.crcsList.find((availableCrc: any) => availableCrc.item_id === crc.id);
        
        if (matchingCrc) {
          return matchingCrc;
        } else {
          return {
            item_id: crc.id,
            item_text: `${crc.id} - ${crc.name}`,
            initials: crc.initials,
            full_name: crc.name,
            first_name: crc.name.split(' ')[0] || '',
            last_name: crc.name.split(' ').slice(1).join(' ') || '',
            designation: crc.designation || 'User'
          };
        }
      });
      this.assignCrcForm.patchValue({
        selectedCrcs: existingCrcs
      });
    }

    this.showModal();
  }

  /**
   * Assign CRCs to study
   */
  assignCrcs(): void {
    if (this.assignCrcForm.valid) {
      const selectedCrcs = this.assignCrcForm.get('selectedCrcs')?.value;
      
      if (!selectedCrcs || selectedCrcs.length === 0) {
        this.assignCrcValidationMessage = 'Please select at least one CRC.';
        return;
      }

      // Transform the selected CRCs to the format expected by the API
      const crcsList = selectedCrcs.map(crc => {
        const crcId = parseInt(crc.item_id);
        
        if (isNaN(crcId)) {
          return null;
        }
        
        return {
          id: crcId,
          name: crc.item_text ? crc.item_text.split(' - ')[1] : crc.full_name || 'Unknown User'
        };
      }).filter(crc => crc !== null);
      
      if (crcsList.length === 0) {
        this.assignCrcValidationMessage = 'No valid users selected. Please check your selections.';
        return;
      }

      this.processCrcAssignment(crcsList, selectedCrcs);
    } else {
      this.assignCrcValidationMessage = 'Please select at least one CRC.';
    }
  }

  /**
   * Process CRC assignment with template ID
   */
  private processCrcAssignment(crcsList: any[], selectedCrcsUi: any[]): void {
    var templateId = null;

    const templateName = this.selectedStudy?.template_list?.[0]?.name;

    const continueWithAssign = (resolvedTemplateId: any) => {
      templateId = resolvedTemplateId || null;

      const studyCode = this.selectedStudy.code || this.selectedStudy.study_code ||
        `STUDY_${this.selectedStudy.id}_${Date.now()}`;

      const payload = {
        study_id: this.selectedStudy.id,
        name: this.selectedStudy.study_name,
        code: studyCode,
        template_id: templateId,
        site_ids: this.selectedStudy.site_list?.map(site => site.site_id) || [],
        status: this.selectedStudy.study_status,
        crcs_list: crcsList
      };

      this.loading = true;

      // Use the new assignCRCs endpoint
      this.apiService.assignCRCs(payload).subscribe(
        (response: any) => {
          console.log('✅ [assignCRCs] Response received:', response);

          // Update UI immediately so user doesn't need a manual refresh.
          this.applyLocalCrcAssignments(selectedCrcsUi);

          window.alert(this.isEditMode ? 'User assignments updated successfully!' : 'CRCs assigned successfully!');
          this.hideModal();
          this.loading = false;
        },
        (error) => {
          console.error('❌ [assignCRCs] Error occurred:', error);
          this.loading = false;
          this.assignCrcValidationMessage = 'Failed to assign CRCs. Please try again.';
        }
      );
    };

    if (!templateName) {
      console.warn('[assignCRCs] Template name missing; proceeding without template_id');
      continueWithAssign(null);
      return;
    }

    console.log(`Sending Template Name to get Template Id: ${templateName}`);
    this.apiService.getTemplateId(templateName).subscribe(
      (response: any) => {
        console.log(`Template Id Found: ${response?.template_id}`);
        continueWithAssign(response?.template_id);
      },
      (error) => {
        console.error('❌ [getTemplateId] Error occurred:', error);
        this.loading = false;
        this.assignCrcValidationMessage = 'Failed to fetch template information. Please try again.';
      }
    );
  }

  /**
   * Show modal dialog
   */
  private showModal(): void {
    const modalElement = document.getElementById('assignCrcModal');
    if (modalElement) {
      this.assignCrcModalInstance = bootstrap.Modal.getOrCreateInstance(modalElement);
      this.assignCrcModalInstance.show();
    }
  }

  /**
   * Hide modal dialog
   */
  private hideModal(): void {
    const modalElement = document.getElementById('assignCrcModal');
    if (modalElement) {
      const bootstrapModal = bootstrap.Modal.getInstance(modalElement) || bootstrap.Modal.getOrCreateInstance(modalElement);
      bootstrapModal.hide();
      // After the hide animation completes, ensure no stale backdrop blocks the page.
      setTimeout(() => this.cleanupStaleModalBackdrop(), 250);
    }
  }

  onAssignModalHidden(): void {
    this.showCustomDropdown = false;
    this.searchTerm = '';
    this.assignCrcValidationMessage = '';
    this.assignCrcForm.reset({ selectedCrcs: [] });
    this.selectedStudy = null;
    this.isEditMode = false;
    this.assignCrcModalInstance = null;
    this.cleanupStaleModalBackdrop();
  }

  private cleanupStaleModalBackdrop(): void {
    // If another modal is open, don't interfere.
    const openModals = document.querySelectorAll('.modal.show');
    if (openModals.length > 0) return;

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');

    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach((b: any) => b?.remove?.());
  }

  private applyLocalCrcAssignments(selectedCrcsUi: any[]): void {
    if (!this.selectedStudy?.id) return;

    const studyId = String(this.selectedStudy.id);
    // Preserve the existing display value if we only have an email locally.
    const existingRow =
      this.data.find((r: any) => String(r.study_id || r.id) === studyId) ||
      this.filteredData.find((r: any) => String(r.study_id || r.id) === studyId) ||
      null;

    const computedAssignedBy = this.Data
      ? (`${this.Data.first_name || ''} ${this.Data.last_name || ''}`.trim() ||
         this.Data.full_name ||
         this.Data.name ||
         this.Data.email ||
         '')
      : '';

    const existingAssignedBy = existingRow?.assigned_by ? String(existingRow.assigned_by) : '';
    const assignedByValue =
      computedAssignedBy.includes('@') && existingAssignedBy && !existingAssignedBy.includes('@')
        ? existingAssignedBy
        : computedAssignedBy;

    const assignedUsers = (selectedCrcsUi || []).map((u: any) => ({
      id: u.item_id,
      name: u.full_name || (u.item_text ? String(u.item_text).split(' - ')[1] : '') || u.name || 'Unknown User',
      initials: u.initials || this.getInitials(u.full_name || u.name || ''),
      date_assigned: new Date().toISOString(),
      assigned_by: assignedByValue
    }));

    const updateRow = (row: any) => {
      row.has_assignments = assignedUsers.length > 0;
      row.assigned_users = assignedUsers;
      if (assignedByValue) row.assigned_by = assignedByValue;
      return row;
    };

    // Update in-memory arrays used by the template.
    const idxData = this.data.findIndex((r: any) => String(r.study_id || r.id) === studyId);
    if (idxData !== -1) this.data[idxData] = updateRow(this.data[idxData]);

    const idxFiltered = this.filteredData.findIndex((r: any) => String(r.study_id || r.id) === studyId);
    if (idxFiltered !== -1) this.filteredData[idxFiltered] = updateRow(this.filteredData[idxFiltered]);

    // Recalculate pagination for the already-filtered data without changing filters.
    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;
    this.updatePageRange();
    this.updatePaginatedData();
  }

  // ===========================================
  // CUSTOM DROPDOWN METHODS
  // ===========================================
  
  /**
   * Toggle custom dropdown visibility
   */
  toggleCustomDropdown(): void {
    this.showCustomDropdown = !this.showCustomDropdown;
    if (this.showCustomDropdown) {
      this.searchTerm = '';
      this.filteredCrcsList = this.filterUsersBySiteLocation(this.currentStudySiteLocation);
    }
  }

  closeCustomDropdown(): void {
    if (this.showCustomDropdown) {
      this.showCustomDropdown = false;
    }
  }

  /**
   * Filter CRCs based on search term and site location
   */
  filterCrcs(): void {
    const siteFilteredUsers = this.filterUsersBySiteLocation(this.currentStudySiteLocation);
    
    if (!this.searchTerm.trim()) {
      this.filteredCrcsList = siteFilteredUsers;
    } else {
      this.filteredCrcsList = siteFilteredUsers.filter((crc: any) =>
        crc.full_name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        crc.initials.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }
  }

  /**
   * Check if CRC is selected
   */
  isCrcSelected(crc: any): boolean {
    const selectedCrcs = this.assignCrcForm.get('selectedCrcs')?.value || [];
    return selectedCrcs.some((selected: any) => selected.item_id === crc.item_id);
  }

  /**
   * Toggle CRC selection
   */
  toggleCrcSelection(crc: any): void {
    const currentSelection = this.assignCrcForm.get('selectedCrcs')?.value || [];
    const isSelected = this.isCrcSelected(crc);
    
    // Prevent user from deselecting themselves in edit mode
    if (isSelected && this.isEditMode && this.isCurrentUser(crc)) {
      return; // Cannot deselect themselves
    }
    
    if (isSelected) {
      const updatedSelection = currentSelection.filter((selected: any) => selected.item_id !== crc.item_id);
      this.assignCrcForm.get('selectedCrcs')?.setValue(updatedSelection);
    } else {
      this.assignCrcForm.get('selectedCrcs')?.setValue([...currentSelection, crc]);
    }
  }

  /**
   * Remove selected CRC
   */
  removeSelectedCrc(crc: any): void {
    // Prevent user from removing themselves in edit mode
    if (this.isEditMode && this.isCurrentUser(crc)) {
      return;
    }
    
    const currentSelection = this.assignCrcForm.get('selectedCrcs')?.value || [];
    const updatedSelection = currentSelection.filter((selected: any) => selected.item_id !== crc.item_id);
    this.assignCrcForm.get('selectedCrcs')?.setValue(updatedSelection);
  }

  /**
   * Check if a CRC is the current logged-in user
   */
  isCurrentUser(crc: any): boolean {
    if (!this.Data || !crc) {
      return false;
    }
    return String(crc.item_id) === String(this.Data.id);
  }

  /**
   * Check if user can remove a CRC (cannot remove themselves in edit mode)
   */
  canRemoveCrc(crc: any): boolean {
    if (!this.isEditMode) {
      return true; // Can remove anyone in assign mode
    }
    // In edit mode, cannot remove themselves
    return !this.isCurrentUser(crc);
  }

  // ===========================================
  // UTILITY METHODS
  // ===========================================
  
  /**
   * Filter users by site location
   */
  filterUsersBySiteLocation(studySiteLocation: string): any[] {
    console.log(`Showing all users (site location filtering disabled)`);
    console.log(`Total users available: ${this.crcsList.length}`);
    return [...this.crcsList];
  }

  /**
   * Get template name for display
   */
  getTemplateName(user: any): string {
    if (user.template_name) {
      return user.template_name;
    }
    
    if (user.template_list && user.template_list.length > 0) {
      return user.template_list[0].name || 'Unknown Template';
    }
    
    return 'No Template';
  }

  /**
   * Get site location from site_list
   */
  getSiteLocation(user: any): string {
    if (user.site_location && user.site_location !== 'No Site') {
      return user.site_location;
    }
    
    if (user.site_list && user.site_list.length > 0) {
      return user.site_list[0].site_name || 'No Site';
    }
    
    return 'No Site';
  }

  /**
   * Get user initials for circle display
   */
  getInitials(name: string): string {
    if (!name || name === 'Not Assigned' || name.trim() === '') {
      return 'NA';
    }
    const names = name.trim().split(' ').filter(n => n.length > 0);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }

  /**
   * Get the earliest date assigned from multiple users
   */
  getEarliestDateAssigned(study: any): string {
    if (!study.assigned_users || study.assigned_users.length === 0) {
      return 'N/A';
    }
    
    const dates = study.assigned_users
      .map((user: any) => user.date_assigned)
      .filter((date: string) => date && date !== 'N/A')
      .sort();
    
    return dates.length > 0 ? dates[0] : 'N/A';
  }

  /**
   * Get assigned by summary for multiple users
   */
  getAssignedBySummary(study: any): string {
    if (!study.assigned_users || study.assigned_users.length === 0) {
      return 'N/A';
    }
    
    const assignedByList = study.assigned_users
      .map((user: any) => user.assigned_by)
      .filter((assignedBy: string) => assignedBy && assignedBy !== 'N/A');
    
    if (assignedByList.length === 0) {
      return 'N/A';
    }
    
    const uniqueAssignedBy = [...new Set(assignedByList)] as string[];
    
    if (uniqueAssignedBy.length === 1) {
      return uniqueAssignedBy[0];
    } else {
      return `${uniqueAssignedBy[0]} +${uniqueAssignedBy.length - 1} more`;
    }
  }

  // ===========================================
  // DATA PROCESSING METHODS
  // ===========================================
  
  /**
   * Group users by study and location
   */
  groupUsersByStudyAndLocation(data: any[], studiesMap: Map<any, any>): any[] {
    const groupedMap = new Map<string, any>();
    
    data.forEach(item => {
      const studyData = studiesMap.get(item.study_id) || {};
      const groupKey = `${item.study_name || ''}_${item.study_code || ''}_${item.site_location || ''}`;
      
      if (groupedMap.has(groupKey)) {
        const existingGroup = groupedMap.get(groupKey);
        existingGroup.assigned_users.push({
          id: item.crc_id,
          name: item.crc_name || 'Unknown User',
          initials: this.getInitials(item.crc_name || 'Unknown User'),
          study_crc_id: item.id,
          date_assigned: item.date_assigned,
          assigned_by: item.assigned_by
        });
      } else {
        groupedMap.set(groupKey, {
          id: item.study_id,
          study_id: item.study_id,
          study_name: item.study_name,
          study_code: item.study_code,
          site_location: item.site_location || this.getSiteLocation(studyData),
          template_name: this.getTemplateName(studyData),
          template_list: studyData.template_list || [],
          site_list: studyData.site_list || [],
          study_status: studyData.study_status || item.study_status,
          has_assignments: true,
          is_crc_row: true,
          assigned_users: [{
            id: item.crc_id,
            name: item.crc_name || 'Unknown User',
            initials: this.getInitials(item.crc_name || 'Unknown User'),
            study_crc_id: item.id,
            date_assigned: item.date_assigned,
            assigned_by: item.assigned_by
          }]
        });
      }
    });
    
    return Array.from(groupedMap.values());
  }

  /**
   * Process CRC studies data and create rows for multiple locations
   */
  processCrcStudiesData(crcResponse: any, studiesMap: Map<any, any>): any[] {
    const users: any[] = [];
    const currentUserId = this.Data?.id;
    
    crcResponse.data.forEach((crcStudy: any) => {
      // Only show studies assigned to the currently logged-in user
      if (crcStudy.crc_id && crcStudy.crc_id.toString() !== currentUserId?.toString()) {
        return;
      }
      
      const studyData = studiesMap.get(crcStudy.study_id) || {};
      
      // Check if this study has multiple locations
      if (studyData.site_list && studyData.site_list.length > 1) {
        studyData.site_list.forEach((site: any, index: number) => {
          users.push({
            ...studyData,
            id: `${crcStudy.id}_${site.site_id}`,
            study_id: crcStudy.study_id,
            study_name: studyData.study_name || 'Unknown Study',
            site_location: site.site_name || 'No Site',
            template_name: this.getTemplateName(studyData),
            template_list: studyData.template_list || [],
            site_list: [site],
            assigned_users: [{
              id: crcStudy.crc_id,
              name: crcStudy.crc_name || 'Unknown User',
              initials: this.getInitials(crcStudy.crc_name || 'Unknown User'),
              study_crc_id: crcStudy.id,
              date_assigned: crcStudy.date_assigned,
              assigned_by: crcStudy.assigned_by
            }],
            has_assignments: true,
            is_crc_row: true,
            study_crc_id: crcStudy.id,
            crc_id: crcStudy.crc_id,
            crc_name: crcStudy.crc_name || 'Unknown User'
          });
        });
      } else {
        users.push({
          ...studyData,
          id: crcStudy.id,
          study_id: crcStudy.study_id,
          study_name: studyData.study_name || 'Unknown Study',
          site_location: crcStudy.site_location || this.getSiteLocation(studyData),
          template_name: this.getTemplateName(studyData),
          template_list: studyData.template_list || [],
          site_list: studyData.site_list || [],
          assigned_users: [{
            id: crcStudy.crc_id,
            name: crcStudy.crc_name || 'Unknown User',
            initials: this.getInitials(crcStudy.crc_name || 'Unknown User'),
            study_crc_id: crcStudy.id,
            date_assigned: crcStudy.date_assigned,
            assigned_by: crcStudy.assigned_by
          }],
          has_assignments: true,
          is_crc_row: true,
          study_crc_id: crcStudy.id,
          crc_id: crcStudy.crc_id,
          crc_name: crcStudy.crc_name || 'Unknown User'
        });
      }
    });
    
    return users;
  }

  // ===========================================
  // TOOLTIP METHODS
  // ===========================================
  
  /**
   * Show custom tooltip
   */
  showTooltip(event: MouseEvent, text: string): void {
    this.hideTooltip();
    
    const tooltip = document.createElement('div');
    tooltip.className = 'custom-tooltip';
    tooltip.textContent = text;
    tooltip.style.cssText = `
      position: absolute;
      background-color: #333;
      color: white;
      padding: 6px 10px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      z-index: 99999;
      pointer-events: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      opacity: 0;
      transition: opacity 0.2s ease;
    `;
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    tooltip.style.left = (rect.left + rect.width / 2) + 'px';
    tooltip.style.top = (rect.top - 25) + 'px';
    tooltip.style.transform = 'translateX(-50%)';
    
    document.body.appendChild(tooltip);
    
    setTimeout(() => {
      tooltip.style.opacity = '1';
    }, 10);
  }

  /**
   * Hide custom tooltip
   */
  hideTooltip(): void {
    const existingTooltip = document.querySelector('.custom-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }
  }

  // ===========================================
  // FREEZE/UNFREEZE METHODS
  // ===========================================

  /**
   * Open freeze/unfreeze modal
   */
  freezeStudy(study: any): void {
    if (!this.canManageFreezeLock()) {
      return;
    }
    this.currentStudy = study;
    this.isFreezing = !study.is_frozen;
    this.freezeEmail = '';
    this.freezePassword = '';
    this.freezeReason = '';
    this.freezeError = '';
    this.showFreezePassword = false;
    
    const modalElement = document.getElementById('freezeStudyModal');
    if (modalElement) {
      const bootstrapModal = new bootstrap.Modal(modalElement);
      bootstrapModal.show();
    }
  }

  /**
   * Toggle freeze password visibility
   */
  toggleFreezePasswordVisibility(): void {
    this.showFreezePassword = !this.showFreezePassword;
  }

  /**
   * Authenticate and freeze/unfreeze study
   */
  authenticateAndFreeze(): void {
    if (!this.freezeEmail || !this.freezePassword || !this.freezeReason) {
      this.freezeError = 'Please enter email, password, and reason';
      return;
    }

    this.freezeError = '';
    this.loading = true;

    const purpose = this.isFreezing ? 'freeze_study' : 'unfreeze_study';
    
    // Authenticate using the login API
    this.apiService.login(this.freezeEmail, this.freezePassword, purpose).subscribe(
      response => {
        // Authentication successful, now freeze/unfreeze the study
        this.updateStudyFreezeStatus();
      },
      error => {
        this.loading = false;
        this.freezeError = 'Invalid email or password. Please try again.';
        console.error('Authentication error:', error);
      }
    );
  }

  /**
   * Update study freeze status
   */
  updateStudyFreezeStatus(): void {
    if (!this.currentStudy) {
      this.loading = false;
      return;
    }

    const updateData = {
      study_id: this.currentStudy.study_id,
      subject_id: this.currentStudy.study_id,
      locked_data_id: this.currentStudy.study_id,
      is_frozen: this.isFreezing,
      is_locked: (this.currentStudy && this.currentStudy.is_locked) ? 1 : 0,
      locked_by: this.freezeEmail,
      reason: this.freezeReason
    };

    // Call lock-field API (freeze/unfreeze; does not change is_locked — use Lock for that)
    this.apiService.updateStudyFreezeStatus(updateData).subscribe(
      response => {
        // Update local data (freeze updates both is_frozen and is_locked via lock-field)
        const studyIndex = this.data.findIndex(s => 
          (s.study_id || s.id) === (this.currentStudy.study_id || this.currentStudy.id)
        );
        if (studyIndex !== -1) {
          this.data[studyIndex].is_frozen = this.isFreezing;
        }
        
        // Update filtered data
        const filteredIndex = this.filteredData.findIndex(s => 
          (s.study_id || s.id) === (this.currentStudy.study_id || this.currentStudy.id)
        );
        if (filteredIndex !== -1) {
          this.filteredData[filteredIndex].is_frozen = this.isFreezing;
        }
        
        this.loading = false;
        this.closeFreezeModal();
        alert(`Study ${this.isFreezing ? 'frozen' : 'unfrozen'} successfully!`);
        this.loadUsers(this.currentPage);
      },
      error => {
        console.error('Error updating study freeze status:', error);
        this.loading = false;
        let errorMessage = 'Failed to update study freeze status. Please try again.';
        if (error.error) {
          if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          } else if (typeof error.error === 'string') {
            errorMessage = error.error;
          }
        }
        alert(errorMessage);
        this.freezeError = errorMessage;
      }
    );
  }

  /**
   * Close freeze modal
   */
  closeFreezeModal(): void {
    const modalElement = document.getElementById('freezeStudyModal');
    if (modalElement) {
      const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
    this.freezeEmail = '';
    this.freezePassword = '';
    this.freezeReason = '';
    this.freezeError = '';
    this.currentStudy = null;
  }

  // ===========================================
  // LOCK METHODS
  // ===========================================

  /**
   * Open lock study modal
   */
  lockStudy(study: any): void {
    if (!this.canManageFreezeLock()) {
      return;
    }
    if (study.is_locked) {
      alert('This study is already locked and cannot be unlocked.');
      return;
    }
    
    this.currentStudy = study;
    this.lockEmail = '';
    this.lockPassword = '';
    this.lockReason = '';
    this.lockError = '';
    this.showLockPassword = false;
    
    const modalElement = document.getElementById('lockStudyModal');
    if (modalElement) {
      const bootstrapModal = new bootstrap.Modal(modalElement);
      bootstrapModal.show();
    }
  }

  /**
   * Toggle lock password visibility
   */
  toggleLockPasswordVisibility(): void {
    this.showLockPassword = !this.showLockPassword;
  }

  /**
   * Authenticate and lock study
   */
  authenticateAndLock(): void {
    if (!this.lockEmail || !this.lockPassword || !this.lockReason) {
      this.lockError = 'Please enter email, password, and reason';
      return;
    }

    this.lockError = '';
    this.loading = true;

    const purpose = 'lock_study';
    
    // Authenticate using the login API
    this.apiService.login(this.lockEmail, this.lockPassword, purpose).subscribe(
      response => {
        // Authentication successful, now lock the study
        this.updateStudyLockStatus();
      },
      error => {
        this.loading = false;
        this.lockError = 'Invalid email or password. Please try again.';
        console.error('Authentication error:', error);
      }
    );
  }

  /**
   * Update study lock status via locked_data (POST /api/lock-field)
   */
  updateStudyLockStatus(): void {
    if (!this.currentStudy) {
      this.loading = false;
      return;
    }

    const studyId = this.currentStudy.study_id || this.currentStudy.id;
    const lockBody = {
      locked_data_id: studyId,
      data_type: 'study',
      study_id: studyId,
      locked_by: this.lockEmail,
      is_locked: 1,
      is_freezed: 0
    };

    this.apiService.lockField(lockBody).subscribe(
      response => {
        // Update local data
        const studyIndex = this.data.findIndex(s => 
          (s.study_id || s.id) === (this.currentStudy.study_id || this.currentStudy.id)
        );
        if (studyIndex !== -1) {
          this.data[studyIndex].is_locked = true;
        }
        
        // Update filtered data
        const filteredIndex = this.filteredData.findIndex(s => 
          (s.study_id || s.id) === (this.currentStudy.study_id || this.currentStudy.id)
        );
        if (filteredIndex !== -1) {
          this.filteredData[filteredIndex].is_locked = true;
        }
        
        this.loading = false;
        this.closeLockModal();
        alert('Study locked successfully! This action cannot be undone.');
        this.loadUsers(this.currentPage);
      },
      error => {
        console.error('Error updating study lock status:', error);
        this.loading = false;
        let errorMessage = 'Failed to lock study. Please try again.';
        if (error.error) {
          if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          } else if (typeof error.error === 'string') {
            errorMessage = error.error;
          }
        }
        alert(errorMessage);
        this.lockError = errorMessage;
      }
    );
  }

  /**
   * Close lock modal
   */
  closeLockModal(): void {
    const modalElement = document.getElementById('lockStudyModal');
    if (modalElement) {
      const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
    this.lockEmail = '';
    this.lockPassword = '';
    this.lockReason = '';
    this.lockError = '';
    this.currentStudy = null;
  }

  // ===========================================
  // UNUSED/LEGACY CODE (MOVED TO BOTTOM)
  // ===========================================
  
  // Legacy properties that are no longer used
  dtOptions: { pagingType: string; pageLength: number; processing: boolean; ajax: (dataTablesParameters: any, callback: any) => void; columns: { title: string; data: string; }[]; };
  studyName: string;
  studyCode: string;
  selectedCRCs: any[] = [];
  studyDuration: number;
  noOfDays: number;
  buffer: number;
  validationMessage: string = '';
  volunteerFrequencyValidationMessage: string = '';
  bufferDays = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
  studyForm: FormGroup;
  generatedDayInputs: number[] = [];
  filteredData: any[] = [];
  paginatedData: any[] = [];
  totalPages: number[] = [];
  pageNumbersToShow: number = 4;

  // Search and filter users based on current filter values
  searchUsers(): void {
    // this.updateQueryParams();
    this.applyFilters();
  }

  // Apply filters to the data
  applyFilters(): void {
    if (!this.data || this.data.length === 0) {
      this.filteredData = [];
      return;
    }

    this.filteredData = this.data.filter(item => {
      // Filter by CRC name
      const matchesCrcName = !this.searchCrcName || 
        (item.crc_name && item.crc_name.toLowerCase().includes(this.searchCrcName.toLowerCase()));

      // Filter by study name
      const matchesStudyName = !this.searchStudyName || 
        (item.study_name && item.study_name.toLowerCase().includes(this.searchStudyName.toLowerCase()));

      // Filter by location
      const matchesLocation = !this.searchLocation || 
        (item.site_location && item.site_location.toLowerCase().includes(this.searchLocation.toLowerCase()));

      // Filter by assigned by
      const matchesAssignedBy = !this.searchAssignedBy || 
        (item.assigned_by && item.assigned_by.toLowerCase().includes(this.searchAssignedBy.toLowerCase()));

      return matchesCrcName && matchesStudyName && matchesLocation && matchesAssignedBy;
    });

    // Update pagination
    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;
    this.currentPage = 1; // Reset to first page when filtering
    this.updatePageRange();
    this.updatePaginatedData();
  }

  calculateTotalPages(): void {
    const pageCount = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalPages = Array.from({ length: pageCount }, (_, i) => i);
  }

  updatePaginatedData(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedData = this.filteredData.slice(start, end);
  }

  extractCrcsFromUserObject(user: any): any[] {
    let crcs: any[] = [];
    
    if (user.crcs_list && Array.isArray(user.crcs_list)) {
      crcs = user.crcs_list.map((crc: any) => {
        const nameParts = crc.name.split(' ');
        const initials = nameParts.length >= 2 
          ? `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase()
          : crc.name.substring(0, 2).toUpperCase();

        return {
          item_id: crc.id,
          item_text: `${crc.id} - ${crc.name} (${crc.designation || 'User'})`,
          initials: initials,
          full_name: crc.name,
          first_name: nameParts[0] || '',
          last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''
        };
      });
    }
    
    if (crcs.length === 0 && user.crc_id && user.crc_name && user.crc_name !== 'Not Assigned') {
      const nameParts = user.crc_name.split(' ');
      const initials = nameParts.length >= 2 
        ? `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase()
        : user.crc_name.substring(0, 2).toUpperCase();

      crcs = [{
        item_id: user.crc_id,
        item_text: `${user.crc_id} - ${user.crc_name} (${user.designation || 'User'})`,
        initials: initials,
        full_name: user.crc_name,
        first_name: nameParts[0] || '',
        last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''
      }];
    }
    
    if (crcs.length === 0 && user.crc_id && user.name && user.name !== 'Not Assigned') {
      const nameParts = user.name.split(' ');
      const initials = nameParts.length >= 2 
        ? `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase()
        : user.name.substring(0, 2).toUpperCase();

      crcs = [{
        item_id: user.crc_id,
        item_text: `${user.crc_id} - ${user.name} (${user.designation || 'User'})`,
        initials: initials,
        full_name: user.name,
        first_name: nameParts[0] || '',
        last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''
      }];
    }
    
    return crcs;
  }

  refreshDropdownSelection(): void {
    const currentValue = this.assignCrcForm.get('selectedCrcs')?.value;
    
    if (currentValue && currentValue.length > 0) {
      this.assignCrcForm.get('selectedCrcs')?.setValue([...currentValue]);
    }
  }

  // Legacy commented code blocks
  /*
  // searchStudies(): void {
  //   // Fetch user data
  //   let userData = this.commonService.getUserData();
  //   if (!userData) {
  //     this.commonService.logout();
  //     return;
  //   }
  //   // ... rest of implementation
  // }

  // applyFilters(): void {
  //   this.searchStudies();
  // }

  // viewStudy(index: number) {
  //   console.log('Selected User Index:', index);
  //   this.router.navigate(['/study-crc-view/'+index]);
  // }

  // EditStudy(index: number): void {
  //   this.router.navigate(['/study-crc-edit/'+index]);
  // }

  // VisitStudy(index: number): void {
  //   this.router.navigate(['/view-visits/'+index]);
  // }

  // MonitorStudy(index: number): void {
  //   this.router.navigate(['/study-crc-monitoring/'+index]);
  // }
  */
}
